import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Users, Target, Calendar, Star, ArrowUpRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIntelligentMatchmaking } from '@/hooks/useIntelligentMatchmaking';
import { useFavorites } from '@/hooks/useFavorites';
import { useOpportunities } from '@/hooks/useOpportunities';
import { useEvents } from '@/hooks/useEvents';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';

const DashboardOverview = () => {
  const { user } = useAuth();
  const { getAnalytics } = useIntelligentMatchmaking({ limit: 5 });
  const { getFavoriteOpportunities } = useFavorites();
  const { opportunities } = useOpportunities();
  const { events } = useEvents();
  const { getAcceptedConnections } = useConnectionStatus(user?.id);

  const matchStats = getAnalytics();
  const favoriteOpportunities = getFavoriteOpportunities();
  
  // Calculate relevant metrics
  const activeOpportunities = opportunities?.filter(opp => opp.status === 'active').length || 0;
  const upcomingEvents = events?.filter(event => 
    new Date(event.start_date) > new Date()
  ).length || 0;
  const acceptedConnections = getAcceptedConnections().length;

  const stats = [
    {
      title: "Connexions Actives",
      value: acceptedConnections,
      description: "Membres dans votre réseau",
      icon: TrendingUp,
      color: "text-green-600",
      bgColor: "bg-green-50",
      trend: "Réseau professionnel"
    },
    {
      title: "Connexions Qualifiées",
      value: matchStats.totalSuggestions,
      description: "Suggestions intelligentes",
      icon: Users,
      color: "text-blue-600", 
      bgColor: "bg-blue-50",
      trend: `${Math.round(matchStats.averageCompatibility)}% compatibilité`
    },
    {
      title: "Opportunités",
      value: activeOpportunities,
      description: "Projets disponibles",
      icon: Target,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      trend: "Mises à jour quotidiennes"
    },
    {
      title: "Événements",
      value: upcomingEvents,
      description: "Prochaines dates",
      icon: Calendar,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      trend: "Cette semaine"
    }
  ];

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat, index) => (
        <Card key={index} className="relative overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
              {stat.title}
            </CardTitle>
            <div className={`p-1.5 sm:p-2 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`h-3 w-3 sm:h-4 sm:w-4 ${stat.color}`} />
            </div>
          </CardHeader>
          <CardContent className="pt-1">
            <div className="text-xl sm:text-2xl font-bold">{stat.value}</div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mb-1 sm:mb-2">
              {stat.description}
            </p>
            <div className="flex items-center text-[10px] sm:text-xs text-muted-foreground">
              <ArrowUpRight className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
              {stat.trend}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DashboardOverview;